import { useEffect, useState, useCallback } from 'react';

export interface UseTTSOptions {
  text: string;
  lang?: string;
  autoGermanFallback?: boolean;
}

interface UseTTS {
  speaking: boolean;
  speak: () => void;
  stop: () => void;
  voiceLoading: boolean;
  voice: SpeechSynthesisVoice | null;
}

export function useTTS(options: UseTTSOptions): UseTTS {
  const { text, lang = 'de-DE', autoGermanFallback = true } = options;
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [voiceLoading, setVoiceLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setVoiceLoading(false);
      return;
    }
    const synth = window.speechSynthesis;
    const selectVoice = () => {
      const voices = synth.getVoices();
      let chosen: SpeechSynthesisVoice | null = null;
      if (autoGermanFallback) {
        chosen = voices.find(v => v.lang?.toLowerCase().startsWith('de')) || null;
      }
      if (!chosen) {
        chosen = voices.find(v => v.lang === lang) || voices[0] || null;
      }
      setVoice(chosen);
      setVoiceLoading(false);
    };
    selectVoice();
    synth.onvoiceschanged = selectVoice;
    return () => {
      synth.onvoiceschanged = null;
    };
  }, [lang, autoGermanFallback]);

  const stop = useCallback(() => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, []);

  const speak = useCallback(() => {
    if (!('speechSynthesis' in window)) return;
    const synth = window.speechSynthesis;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.lang = voice?.lang || lang;
    u.onend = () => setSpeaking(false);
    synth.speak(u);
    setSpeaking(true);
  }, [text, voice, lang]);

  return { speaking, speak, stop, voiceLoading, voice };
}
export default useTTS;
